import {
  GetObjectCommand,
  GetObjectCommandInput,
  GetObjectCommandOutput,
  PutObjectCommand,
  PutObjectCommandInput,
  PutObjectCommandOutput,
  S3Client as S3v3Client,
  S3ClientConfig,
} from '@aws-sdk/client-s3'
import {getSignedUrl} from '@aws-sdk/s3-request-presigner'

import {authAdapter} from '@/services/auth-adapter'
import {sanitizeForS3} from '@/utils/utils'

/**
 * Converts the Body stream from S3 GetObject response to a string
 * @param {ReadableStream | Blob | string | Uint8Array} body - The Body from S3 GetObjectCommandOutput
 * @returns {Promise<string>} - The body content as a string
 */
export async function streamToString(body: any): Promise<string> {
  if (typeof body === 'string') {
    return body
  }

  if (body instanceof Uint8Array) {
    return new TextDecoder().decode(body)
  }

  // For AWS SDK v3 in Node.js environment
  if (body && typeof body.transformToString === 'function') {
    return await body.transformToString()
  }

  // For ReadableStream in browser (AWS SDK v3 in browser)
  if (body && typeof body.getReader === 'function') {
    const reader = body.getReader()
    const chunks: Uint8Array[] = []

    try {
      let done = false
      while (!done) {
        const readResult = await reader.read()
        done = readResult.done
        if (!done && readResult.value) {
          chunks.push(readResult.value)
        }
      }

      // Combine all chunks into a single Uint8Array
      const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0)
      const combined = new Uint8Array(totalLength)
      let offset = 0
      for (const chunk of chunks) {
        combined.set(chunk, offset)
        offset += chunk.length
      }

      return new TextDecoder().decode(combined)
    } finally {
      reader.releaseLock()
    }
  }

  // Fallback for older SDK or Buffer
  if (body && typeof body.toString === 'function') {
    return body.toString()
  }

  throw new Error('Unable to convert body to string')
}

const {VUE_APP_AWS_REGION} = process.env

export default class S3Client {
  private s3: S3v3Client

  constructor(opts: S3ClientConfig) {
    this.s3 = new S3v3Client(opts)
  }

  getObject(params: GetObjectCommandInput): Promise<GetObjectCommandOutput> {
    const command = new GetObjectCommand(params)
    return this.s3.send(command)
  }

  async putObject(params: PutObjectCommandInput): Promise<PutObjectCommandOutput> {
    const command = new PutObjectCommand(params)
    return await this.s3.send(command)
  }

  async upload({
    bucket,
    key,
    body,
    userName,
    email,
  }: {
    bucket: string
    key: string
    body: Uint8Array | string
    userName?: string
    email?: string
  }): Promise<void> {
    const metadata: Record<string, string> = {}

    if (userName) metadata['created-by-name'] = sanitizeForS3(userName)
    if (email) metadata['created-by-email'] = email

    try {
      await this.putObject({
        Bucket: bucket,
        Key: key,
        Body: body,
        Metadata: metadata,
      })
    } catch (err) {
      console.error('Error uploading object to S3', {
        bucket,
        key,
        err,
      })
      throw err
    }
  }

  async getSignedUrl(
    params: GetObjectCommandInput,
    expiresIn = 3600, // 1h by default
  ): Promise<string> {
    const command = new GetObjectCommand(params)
    return await getSignedUrl(this.s3, command, {expiresIn})
  }
}

// Cache to avoid re-creating S3 client unnecessarily
let s3ClientCache: S3Client | null = null

export const s3 = async (): Promise<S3Client> => {
  try {
    if (s3ClientCache) return s3ClientCache

    // Use authAdapter instead of fetchAuthSession directly
    // This ensures OIDC authentication is properly handled
    const session = await authAdapter.getCurrentSession()

    if (!session.isAuthenticated || !session.credentials) {
      throw new Error('User is not authenticated or credentials are missing')
    }

    const clientConfig: S3ClientConfig = {
      credentials: session.credentials,
      region: VUE_APP_AWS_REGION,
    }
    s3ClientCache = new S3Client(clientConfig)
    return s3ClientCache
  } catch (err) {
    console.error('ERROR: Failed to initialize S3Client with Amplify credentials.', err)
    throw new Error('Authentication failure: Could not obtain AWS credentials for S3 access.')
  }
}
